import React from 'react';

import { ActionsMenu, bemBlock, Button, Dropdown } from '@neptune/shared/venus-ui';

import { ColorPicker } from './ColorPicker';
import { useColorPicker } from './useColorPicker';

import './ColorPickerDropdown.less';

const block = bemBlock('color-picker-dropdown');

type NewColorPickerDropdownBaseProps = {
  color?: string;
  className?: string;
  presetColors?: string[];
  trigger: React.ReactNode;
  onAddPresetColor?: (color: string) => void;
};

type NewColorPickerDropdownClearableProps = NewColorPickerDropdownBaseProps & {
  clearable: true;
  onChange?: (color: string | undefined) => void;
};

type NewColorPickerDropdownStandardProps = NewColorPickerDropdownBaseProps & {
  clearable?: false;
  onChange?: (color: string) => void;
};

type NewColorPickerDropdownProps =
  | NewColorPickerDropdownStandardProps
  | NewColorPickerDropdownClearableProps;

export function NewColorPickerDropdown(props: NewColorPickerDropdownStandardProps): JSX.Element;
export function NewColorPickerDropdown(props: NewColorPickerDropdownClearableProps): JSX.Element;

export function NewColorPickerDropdown({
  className,
  color,
  presetColors,
  onChange,
  clearable,
  onAddPresetColor,
  trigger,
}: NewColorPickerDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const { handleExpand, handleCollapse, restoreColor, handleOnChange } = useColorPicker({
    color,
    onAddPresetColor,
    onChange,
    clearable,
  } as NewColorPickerDropdownProps);

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setIsOpen(isOpen);

      if (!isOpen) {
        handleCollapse();
      } else {
        handleExpand();
      }
    },
    [handleCollapse, handleExpand],
  );

  return (
    <ActionsMenu.Sub open={isOpen} onOpenChange={handleOpenChange}>
      <ActionsMenu.SubTrigger asChild>{trigger}</ActionsMenu.SubTrigger>
      <ActionsMenu.Portal>
        <ActionsMenu.SubContent>
          <ColorPicker
            className={className}
            defaultValue={color}
            onChange={handleOnChange}
            onCancel={restoreColor}
            presetColors={presetColors}
          />
          {clearable && onChange && (
            <>
              <Dropdown.Separator />
              <Button
                className={block('reset-button')}
                variant="transparent"
                onClick={() => {
                  onChange(undefined);
                  handleOpenChange(false);
                }}
              >
                Reset to defaults
              </Button>
            </>
          )}
        </ActionsMenu.SubContent>
      </ActionsMenu.Portal>
    </ActionsMenu.Sub>
  );
}
